import type {Bidders} from "header-bidding-base/modules/header-bidding/domain/bidders";
import type {RichAudienceConfig} from "header-bidding-base/modules/config/domain/bidders/richAudienceConfig";
import type {AppNexusConfig} from "header-bidding-base/modules/config/domain/bidders/appNexusConfig";
import type {TeadsConfig} from "header-bidding-base/modules/config/domain/bidders/teadsConfig";
import type {RichAudienceAdUnit} from "header-bidding-base/modules/config/domain/bidders/richAudienceConfig";
import type {AppNexusAdUnit} from "header-bidding-base/modules/config/domain/bidders/appNexusConfig";
import type {TeadsAdUnit} from "header-bidding-base/modules/config/domain/bidders/teadsConfig";
import type {OguryAdUnit, OguryConfig} from "header-bidding-base/modules/config/domain/bidders/oguryConfig";
import type {PubMaticAdUnit, PubMaticConfig} from "header-bidding-base/modules/config/domain/bidders/pubMaticConfig";
import type {RubiconAdUnit, RubiconConfig} from "header-bidding-base/modules/config/domain/bidders/rubiconConfig";
import type {TripleLiftAdUnit, TripleLiftConfig} from "header-bidding-base/modules/config/domain/bidders/tripleLiftConfig";
import type {HeaderBiddingPlatforms} from "header-bidding-base/modules/header-bidding/domain/platforms";
import type {AmazonTamConfig} from "header-bidding-base/modules/config/domain/platforms/amazonTamConfig";
import type {PrebidConfig} from "header-bidding-base/modules/config/domain/platforms/prebidConfig";
import type {IAdUnitTypeDTO, IConfigDTO} from "services-comun-meteored/modules/services/header-bidding/interface";
import type {CriteoAdUnit, CriteoConfig} from "header-bidding-base/modules/config/domain/bidders/criteoConfig";
import type {
    IndexExchangeAdUnit,
    IndexExchangeConfig
} from "header-bidding-base/modules/config/domain/bidders/indexExchangeConfig";
import type {Nexx360AdUnit, Nexx360Config} from "header-bidding-base/modules/config/domain/bidders/nexx360Config";
import type {OnetagAdUnit, OnetagConfig} from "header-bidding-base/modules/config/domain/bidders/onetagConfig";
import type {
    SmartAdServerAdUnit,
    SmartAdServerConfig
} from "header-bidding-base/modules/config/domain/bidders/smartAdServerConfig";
import {MediaNetAdUnit, MediaNetConfig} from "header-bidding-base/modules/config/domain/bidders/mediaNetConfig";
import {AkceloAdUnit, AkceloConfig} from "header-bidding-base/modules/config/domain/bidders/akceloConfig";
import {SeedtagAdUnit, SeedtagConfig} from "header-bidding-base/modules/config/domain/bidders/seedtagConfig";
import {MonetixAdsAdUnit, MonetixAdsConfig} from "header-bidding-base/modules/config/domain/bidders/monetixAdsConfig";
import {SparteoAdUnit, SparteoConfig} from "header-bidding-base/modules/config/domain/bidders/sparteoConfig";
import {OpenXAdUnit, OpenXConfig} from "header-bidding-base/modules/config/domain/bidders/openxConfig";
import {
    TheTradeDeskAdUnit,
    TheTradeDeskConfig
} from "header-bidding-base/modules/config/domain/bidders/theTradeDeskConfig";
import {TappxAdUnit, TappxConfig} from "header-bidding-base/modules/config/domain/bidders/tappxConfig";
import {
    OptidigitalAdUnit,
    OptidigitalConfig
} from "header-bidding-base/modules/config/domain/bidders/optidigitalConfig";

export interface HeaderBiddingAdUnit {
    id: number;
    adUnitPath?: string;
    divId?: string;
    type: IAdUnitTypeDTO;
    bidders: {
        [Bidders.RICHAUDIENCE]?: RichAudienceAdUnit;
        [Bidders.APPNEXUS]?: AppNexusAdUnit;
        [Bidders.TEADS]?: TeadsAdUnit;
        [Bidders.OGURY]?: OguryAdUnit;
        [Bidders.PUBMATIC]?: PubMaticAdUnit;
        [Bidders.RUBICON]?: RubiconAdUnit;
        [Bidders.TRIPLELIFT]?: TripleLiftAdUnit;
        [Bidders.CRITEO]?: CriteoAdUnit;
        [Bidders.INDEXEXCHANGE]?: IndexExchangeAdUnit;
        [Bidders.NEXX360]?: Nexx360AdUnit;
        [Bidders.ONETAG]?: OnetagAdUnit;
        [Bidders.SMARTADSERVER]?: SmartAdServerAdUnit;
        [Bidders.MEDIANET]?: MediaNetAdUnit;
        [Bidders.AKCELO]?: AkceloAdUnit;
        [Bidders.SEEDTAG]?: SeedtagAdUnit;
        [Bidders.MONETIXADS]?: MonetixAdsAdUnit;
        [Bidders.SPARTEO]?: SparteoAdUnit;
        [Bidders.OPENX]?: OpenXAdUnit;
        [Bidders.THETRADEDESK]?: TheTradeDeskAdUnit;
        [Bidders.TAPPX]?: TappxAdUnit;
        [Bidders.OPTIDIGITAL]?: OptidigitalAdUnit;
    }
}

export interface HeaderBiddingConfigProps {
    timeout: number;
    failsafeTimeout: number;
    platformsEnabled: HeaderBiddingPlatforms[];
    biddersEnabled: Bidders[];
    biddersConfig?: {
        [Bidders.RICHAUDIENCE]?: RichAudienceConfig;
        [Bidders.APPNEXUS]?: AppNexusConfig;
        [Bidders.TEADS]?: TeadsConfig;
        [Bidders.OGURY]?: OguryConfig;
        [Bidders.PUBMATIC]?: PubMaticConfig;
        [Bidders.RUBICON]?: RubiconConfig;
        [Bidders.TRIPLELIFT]?: TripleLiftConfig;
        [Bidders.CRITEO]?: CriteoConfig;
        [Bidders.INDEXEXCHANGE]?: IndexExchangeConfig;
        [Bidders.NEXX360]?: Nexx360Config;
        [Bidders.ONETAG]?: OnetagConfig;
        [Bidders.SMARTADSERVER]?: SmartAdServerConfig;
        [Bidders.MEDIANET]?: MediaNetConfig;
        [Bidders.AKCELO]?: AkceloConfig;
        [Bidders.SEEDTAG]?: SeedtagConfig;
        [Bidders.MONETIXADS]?: MonetixAdsConfig;
        [Bidders.SPARTEO]?: SparteoConfig;
        [Bidders.OPENX]?: OpenXConfig;
        [Bidders.THETRADEDESK]?: TheTradeDeskConfig;
        [Bidders.TAPPX]?: TappxConfig;
        [Bidders.OPTIDIGITAL]?: OptidigitalConfig;
    };
    platformsConfig: {
        [HeaderBiddingPlatforms.AMAZON_TAM]?: AmazonTamConfig
        [HeaderBiddingPlatforms.PREBID]?: PrebidConfig
    }
    adUnits: HeaderBiddingAdUnit[]
}

interface CreateHeaderBiddingConfigProps extends Omit<HeaderBiddingConfigProps, 'timeout' | 'failsafeTimeout'>{
    timeout?: number;
    failsafeTimeout?: number;
}

const defaultHeaderBiddingConfigProps = {
    timeout: 750,
    failsafeTimeout: 1500,
}

export class HeaderBiddingConfig {

    private constructor(private props: HeaderBiddingConfigProps) {

    }

    public static create(props: CreateHeaderBiddingConfigProps) {
        return new this({
            timeout: props.timeout || defaultHeaderBiddingConfigProps.timeout,
            failsafeTimeout: props.failsafeTimeout || defaultHeaderBiddingConfigProps.failsafeTimeout,
            ...props
        });
    }

    public static createFromDTO(dto: IConfigDTO): HeaderBiddingConfig {

        const biddersConfig: HeaderBiddingConfigProps['biddersConfig'] = {};
        for (const bidder of dto.config.hb_config.bidders_enabled) {
            biddersConfig[bidder.code as Bidders] = bidder.config ?{
                secondary_bidder: (bidder.config as any).secondary_bidder, // todo quitar esta ñapa
                multiplier: bidder.config.multiplier,
                ...bidder.config.config
            } : undefined;
        }

        const platformsConfig: HeaderBiddingConfigProps['platformsConfig'] = {};
        for (const platform of dto.config.hb_config.platforms_enabled) {
            platformsConfig[platform.code as HeaderBiddingPlatforms] = {
                ...platform.config as any
            };
        }

        const adUnitsConfig: HeaderBiddingConfigProps['adUnits'] = [];
        for (const adUnit of dto.config.hb_config.adunits) {
            const bidders: HeaderBiddingAdUnit['bidders'] = {};
            for (const bidder of adUnit.bidders) {
                bidders[bidder.code_bidder as Bidders] = bidder.config as any;
            }

            adUnitsConfig.push({
                id: adUnit.id_adunit,
                adUnitPath: adUnit.adunit_path,
                divId: adUnit.div_id,
                type: adUnit.type,
                bidders: bidders
            });
        }

        return this.create({
            timeout: dto.config.hb_config.bid_timeout,
            failsafeTimeout: dto.config.hb_config.failsafe_timeout,
            platformsEnabled: dto.config.hb_config.platforms_enabled.map(p => p.code as HeaderBiddingPlatforms),
            biddersEnabled: dto.config.hb_config.bidders_enabled.map(b => b.code as Bidders),
            biddersConfig: biddersConfig,
            platformsConfig: platformsConfig,
            adUnits: adUnitsConfig
        });
    }

    public get timeout(): number {
        return this.props.timeout;
    }

    public get failsafeTimeout(): number {
        return this.props.failsafeTimeout;
    }

    public get biddersEnabled(): Bidders[] {
        return this.props.biddersEnabled;
    }

    public get biddersConfig() {
        return this.props.biddersConfig;
    }

    public get adUnits() {
        return this.props.adUnits;
    }

    public get platformsEnabled(): HeaderBiddingPlatforms[] {
        return this.props.platformsEnabled;
    }

    public get platformsConfig() {
        return this.props.platformsConfig;
    }

    public get config(): HeaderBiddingConfigProps {
        return this.props;
    }

}
